import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { CreatePoolParkingConfigRequest } from '../../../api/gen/model/createPoolParkingConfigRequest';
import { PoolOverflowBehavior } from '../../../api/gen/model/poolOverflowBehavior';
import { AccessWindowFieldComponent, AccessWindowValue } from '../../shared/access-window-field/access-window-field.component';
import {
    JjamesButtonComponent, JJamesCardComponent,
    JJamesAlertComponent, dateInputValueToIso
} from 'shared-lib';

@Component({
    selector: 'app-pool-config-add',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        JjamesButtonComponent,
        JJamesCardComponent,
        JJamesAlertComponent,
        AccessWindowFieldComponent
    ],
    templateUrl: './pool-config-add.component.html',
    styleUrls: ['./pool-config-add.component.scss']
})
export class PoolConfigAddComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly orgService = inject(OrganizationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    configForm!: FormGroup;
    orgId: string | null = null;
    isSubmitting = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;

    /** Optional daily pool-access window (camera-local). Empty = pool valid 24/7. */
    poolWindow = signal<AccessWindowValue>({ start: null, end: null, days: null });

    /** Pool-full overflow policy. Deny is the backend default (fall back to short-term / refuse). */
    readonly overflowBehaviors: ReadonlyArray<{ value: PoolOverflowBehavior; label: string; hint: string }> = [
        { value: PoolOverflowBehavior.Deny, label: 'Deny', hint: 'Pool full → admit as short-term (billed at the short-term tariff) if the space offers it, otherwise refuse entry.' },
        { value: PoolOverflowBehavior.OrgPays, label: 'Organization pays', hint: 'Admit the overflow stay; the organization is billed at the pool tariff and the gate opens free to the parker.' },
        { value: PoolOverflowBehavior.ParkerPays, label: 'Parker pays', hint: 'Admit the overflow stay; the parker pays the pool tariff at exit (gate held until paid).' }
    ];

    ngOnInit(): void {
        this.orgId = this.route.snapshot.paramMap.get('orgId');
        this.configForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
            description: [''],
            maxCarsAllowed: [1, [Validators.required, Validators.min(1)]],
            validFrom: ['', Validators.required],
            validTo: [''],
            overflowBehavior: [PoolOverflowBehavior.Deny, Validators.required]
        });
    }

    onSubmit(): void {
        if (this.configForm.valid && !this.isSubmitting && this.orgId) {
            this.isSubmitting = true;
            this.errorMessage = null;

            const formVal = this.configForm.value;
            const request: CreatePoolParkingConfigRequest = {
                name: formVal.name,
                description: formVal.description || undefined,
                maxCarsAllowed: formVal.maxCarsAllowed,
                // Local midnight, matching the edit form. new Date('2021-02-07') parses the
                // date-only form as UTC midnight, so create and edit would store different
                // instants for the same calendar day.
                // Non-null: validFrom carries Validators.required and this block only runs when the
                // form is valid, so the conversion always yields an instant.
                validFrom: dateInputValueToIso(formVal.validFrom)!,
                validTo: dateInputValueToIso(formVal.validTo) ?? undefined,
                overflowBehavior: formVal.overflowBehavior ?? PoolOverflowBehavior.Deny,
                poolAccessWindowStart: this.poolWindow().start,
                poolAccessWindowEnd: this.poolWindow().end,
                poolAccessWindowDays: this.poolWindow().days
            };

            this.orgService.organizationsOrganizationIdPoolParkingConfigsPost(this.orgId, request).subscribe({
                next: (created) => {
                    this.isSubmitting = false;
                    this.router.navigate(['/org-management', this.orgId, 'pool-config', created.id]);
                },
                error: (error: any) => {
                    this.isSubmitting = false;
                    this.errorMessage = error.error?.title || 'An error occurred while adding the pool parking config.';
                    console.error('Error adding pool config:', error);
                }
            });
        } else {
            this.markFormGroupTouched(this.configForm);
        }
    }

    onCancel(): void {
        if (this.orgId) {
            this.router.navigate(['/org-management', this.orgId]);
        } else {
            this.router.navigate(['/org-management']);
        }
    }

    onClearError(): void {
        this.errorMessage = null;
    }

    onClearSuccess(): void {
        this.successMessage = null;
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.configForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.configForm.get(fieldName);
        if (field?.errors?.['required']) return `This field is required`;
        if (field?.errors?.['min']) return `Minimum value is ${field.errors['min'].min}`;
        if (field?.errors?.['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters`;
        if (field?.errors?.['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} characters`;
        return '';
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
        });
    }
}
